import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { PremiumFeature } from "@/components-legacy/premium-feature";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right.ts";
import { cn } from "@/utils/cn.ts";
import { SettingsFormHeader } from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import { Card, CardContent } from "@babackapp/ui/components/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@babackapp/ui/components/collapsible";
import { Input } from "@babackapp/ui/components/input";
import { ChevronRight, Search } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { PortalWordingItem } from "./portal-wording-item.tsx";
import {
  SECTION_LABELS,
  SECTION_ORDER,
  WIDGET_KEYS,
  type WidgetKeyDescriptor,
  type WidgetKeySection,
} from "./widget-keys.ts";
import { useTranslationMultiLangSearch } from "./use-translation-defaults.ts";
import type {
  PortalCustomTextEntry,
  PortalUserPortalWithCustomTexts,
} from "./types.ts";

type CustomTextEntry = PortalCustomTextEntry;

function overrideStatus(entry: CustomTextEntry | undefined, activeLanguages: string[]) {
  if (!entry?.texts?.length) return "none" as const;

  const nonEmpty = entry.texts.filter((t) => (t.text ?? "").trim().length > 0);
  if (!nonEmpty.length) return "none" as const;

  const normalized = new Set(nonEmpty.map((t) => t.lang.toLowerCase()));
  const allFilled = activeLanguages.every((l) =>
    normalized.has(l.toLowerCase()),
  );

  return allFilled ? "full" : "partial";
}

export function PortalWordingCard() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const hasAdminRight = useViewerHasAtLeastAdminRights();

  const [query, setQuery] = useState("");
  const [selectedSection, setSelectedSection] = useState<
    WidgetKeySection | "all"
  >("all");
  const [expandedSections, setExpandedSections] = useState<Set<WidgetKeySection>>(new Set());

  const FULL_LOCALE_CODES = new Set(["pt-br", "pt-pt", "zh-hans", "zh-hant"]);
  const langCodes = useMemo(() => {
    const codes = shop.preferences.languages.map((lang: string) => {
      const lower = lang.toLowerCase();
      return FULL_LOCALE_CODES.has(lower) ? lower : lower.split("-")[0];
    });
    return [...new Set(["en", ...codes])];
  }, [shop.preferences.languages]);

  const { matchesAny, isLoaded } = useTranslationMultiLangSearch(langCodes);

  const disabled =
    !hasAdminRight || !shop.plan.customisation
      ? t("settings.shop.errors.notAdmin")
      : false;

  const userPortal = shop.preferences
    .userPortal as PortalUserPortalWithCustomTexts;

  const customTextsByKey = useMemo(() => {
    const map = new Map<string, CustomTextEntry>();
    for (const entry of userPortal.customTexts ?? []) {
      map.set(entry.key, entry);
    }
    return map;
  }, [userPortal.customTexts]);

  const activeLanguages = shop.preferences.languages;

  const isFiltering = query.trim().length > 0 || selectedSection !== "all";

  const groupedKeys = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = WIDGET_KEYS.filter((key) => {
      if (selectedSection !== "all" && key.section !== selectedSection) {
        return false;
      }
      if (!normalizedQuery) return true;
      return (
        key.key.toLowerCase().includes(normalizedQuery) ||
        key.label.toLowerCase().includes(normalizedQuery) ||
        matchesAny(key.key, normalizedQuery)
      );
    });

    const bySection = new Map<WidgetKeySection, WidgetKeyDescriptor[]>();
    for (const key of filtered) {
      const arr = bySection.get(key.section) ?? [];
      arr.push(key);
      bySection.set(key.section, arr);
    }
    return bySection;
  }, [query, selectedSection, matchesAny, isLoaded]);

  const totalMatches = useMemo(
    () =>
      [...groupedKeys.values()].reduce((acc, arr) => acc + arr.length, 0),
    [groupedKeys],
  );

  const toggleSection = (section: WidgetKeySection) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  return (
    <Card>
      <CardContent className="py-8 flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <SettingsFormHeader
            title="Wording du portail"
            description="Personnalisez, clé par clé, les textes du portail shopper. Sans personnalisation, la traduction par défaut est utilisée."
          />
          {disabled ? <PremiumFeature /> : null}
        </div>

        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une clé (ex: articleSelection.title)"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <SectionChip
              active={selectedSection === "all"}
              onClick={() => setSelectedSection("all")}
            >
              Toutes
            </SectionChip>
            {SECTION_ORDER.map((section) => (
              <SectionChip
                key={section}
                active={selectedSection === section}
                onClick={() => setSelectedSection(section)}
              >
                {SECTION_LABELS[section]}
              </SectionChip>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {totalMatches} clés sur {WIDGET_KEYS.length}
        </p>

        <div className="flex flex-col gap-2">
          {SECTION_ORDER.filter((section) => groupedKeys.has(section)).map(
            (section) => {
              const keys = groupedKeys.get(section) ?? [];
              const overrideCount = keys.filter((d) => {
                const entry = customTextsByKey.get(d.key);
                return overrideStatus(entry, activeLanguages) !== "none";
              }).length;

              const sectionItems = keys.map((descriptor) => {
                const entry = customTextsByKey.get(descriptor.key);
                const status = overrideStatus(entry, activeLanguages);
                return (
                  <PortalWordingItem
                    key={descriptor.key}
                    descriptor={descriptor}
                    override={entry}
                    status={status}
                    disabled={disabled}
                  />
                );
              });

              if (isFiltering) {
                return (
                  <div key={section} className="flex flex-col gap-2">
                    <h4 className="text-sm font-semibold text-muted-foreground">
                      {SECTION_LABELS[section]}
                    </h4>
                    <div className="flex flex-col gap-1">{sectionItems}</div>
                  </div>
                );
              }

              const isOpen = expandedSections.has(section);

              return (
                <Collapsible
                  key={section}
                  open={isOpen}
                  onOpenChange={() => toggleSection(section)}
                >
                  <CollapsibleTrigger className="flex items-center gap-2 w-full text-left px-2 py-2 rounded-md hover:bg-accent transition-colors">
                    <ChevronRight
                      className={cn(
                        "size-4 text-muted-foreground transition-transform shrink-0",
                        isOpen && "rotate-90",
                      )}
                    />
                    <h4 className="text-sm font-semibold">
                      {SECTION_LABELS[section]}
                    </h4>
                    <div className="flex items-center gap-2 ml-auto text-xs text-muted-foreground">
                      {overrideCount > 0 && (
                        <span className="text-green-600 font-medium">
                          {overrideCount} perso.
                        </span>
                      )}
                      <span>{keys.length} clés</span>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col gap-1 mt-1 pl-2">
                      {sectionItems}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            },
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SectionChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors cursor-pointer",
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-border text-muted-foreground hover:bg-accent",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
